import React, { useState } from 'react';
import { GridCell as GridCellType, SplitPattern, countContentInCell, canSplit } from './types';
import { SignatureBlock, SignatureDynamicData } from '../blocks/types';
import { CellToolbar } from './CellToolbar';
import { AddContentPopover, BlockType } from './AddContentPopover';
import { DeleteCellDialog } from './DeleteCellDialog';
import { SplitPatternPicker } from './SplitPatternPicker';
import { ImageBlockDisplay } from '../blocks/ImageBlock';
import { TextBlockDisplay } from '../blocks/TextBlock';
import { ContactRowBlockDisplay } from '../blocks/ContactRowBlock';
import { SocialIconsBlockDisplay } from '../blocks/SocialIconsBlock';
import { DividerBlockDisplay } from '../blocks/DividerBlock';
import { SpacerBlockDisplay } from '../blocks/SpacerBlock';
import { Plus, Columns, Rows } from 'lucide-react';
import { processDynamicFields } from '@/components/cost-center/document-defaults/dynamicFieldProcessor';

interface GridCellProps {
  cell: GridCellType;
  dynamicData: SignatureDynamicData;
  selectedCellId: string | null;
  onSelect: (cellId: string) => void;
  onAddContent: (cellId: string, blockType: BlockType) => void;
  onSplit: (cellId: string, direction: 'horizontal' | 'vertical', widths: string[]) => void;
  onDelete: (cellId: string) => void;
  isRoot?: boolean;
}

export const GridCellComponent: React.FC<GridCellProps> = ({
  cell,
  dynamicData,
  selectedCellId,
  onSelect,
  onAddContent,
  onSplit,
  onDelete,
  isRoot = false,
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const isSelected = cell.id === selectedCellId;
  const hasContent = cell.content !== null;
  const hasChildren = cell.children !== null && cell.children.length > 0;
  const contentCount = countContentInCell(cell);
  const canCellSplit = canSplit(cell);

  const processText = (text: string) => processDynamicFields(text, { user: dynamicData.user, company: dynamicData.company });

  const handleDelete = () => {
    if (contentCount > 0) {
      setShowDeleteDialog(true);
    } else {
      onDelete(cell.id);
    }
  };

  const handleConfirmDelete = () => {
    onDelete(cell.id);
  };

  const handleSplit = (direction: 'horizontal' | 'vertical', pattern: SplitPattern) => {
    onSplit(cell.id, direction, pattern.widths);
  };

  const renderBlockContent = (block: SignatureBlock) => {
    switch (block.type) {
      case 'image':
        return <ImageBlockDisplay block={block} />;
      case 'text':
        return <TextBlockDisplay block={block} processedContent={processText(block.content)} />;
      case 'contact':
        return <ContactRowBlockDisplay block={block} processedLabel={processText(block.label)} />;
      case 'social':
        return <SocialIconsBlockDisplay block={block} />;
      case 'divider':
        return <DividerBlockDisplay block={block} />;
      case 'spacer':
        return <SpacerBlockDisplay block={block} />;
      default:
        return null;
    }
  };

  // Render children if this cell is split
  if (hasChildren && cell.children) {
    const isHorizontal = cell.splitDirection === 'horizontal';
    
    // Build grid template from widths
    const gridTemplate = isHorizontal 
      ? cell.widths?.join(' ') || 'repeat(auto-fit, 1fr)'
      : `repeat(${cell.children.length}, auto)`;
    
    return (
      <div
        style={{ 
          display: 'grid',
          gridTemplateColumns: isHorizontal ? gridTemplate : '1fr',
          gridTemplateRows: isHorizontal ? 'auto' : gridTemplate,
          gap: '4px',
          width: '100%',
          minWidth: 0,
        }}
      >
        {cell.children.map((childCell) => (
          <div
            key={childCell.id}
            style={{
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <GridCellComponent
              cell={childCell}
              dynamicData={dynamicData}
              selectedCellId={selectedCellId}
              onSelect={onSelect}
              onAddContent={onAddContent}
              onSplit={onSplit}
              onDelete={onDelete}
            />
          </div>
        ))}
      </div>
    );
  }

  // Empty cell - show add content button and split options
  if (!hasContent) {
    return (
      <>
        <div
          className={`
            relative flex flex-col items-center justify-center gap-2 p-4 min-h-[80px] w-full
            border-2 border-dashed rounded-lg transition-all cursor-pointer
            ${isSelected ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-muted-foreground/50'}
          `}
          style={{ minWidth: 0 }}
          onClick={(e) => { e.stopPropagation(); onSelect(cell.id); }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <AddContentPopover onSelect={(type) => onAddContent(cell.id, type)}>
            <button className="flex items-center justify-center w-10 h-10 rounded-full bg-muted hover:bg-muted/80 transition-colors">
              <Plus className="h-5 w-5 text-muted-foreground" />
            </button>
          </AddContentPopover>
          
          {canCellSplit && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <SplitPatternPicker
                direction="horizontal"
                onSelect={(pattern) => handleSplit('horizontal', pattern)}
              >
                <button className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors">
                  <Columns className="h-3 w-3" />
                  <span>Split</span>
                </button>
              </SplitPatternPicker>
              <span>or</span>
              <SplitPatternPicker
                direction="vertical"
                onSelect={(pattern) => handleSplit('vertical', pattern)}
              >
                <button className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors">
                  <Rows className="h-3 w-3" />
                  <span>Stack</span>
                </button>
              </SplitPatternPicker>
            </div>
          )}
          
          {!isRoot && isHovered && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
              className="absolute top-1 right-1 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
              title="Delete cell"
            >
              <span className="text-xs">×</span>
            </button>
          )}
        </div>
        
        <DeleteCellDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleConfirmDelete}
          contentCount={contentCount}
        />
      </>
    );
  }

  // Cell with content
  return (
    <>
      <div
        className={`
          relative group rounded-lg transition-all cursor-pointer p-2 w-full
          ${isSelected ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}
        `}
        style={{ minWidth: 0 }}
        onClick={(e) => { e.stopPropagation(); onSelect(cell.id); }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {renderBlockContent(cell.content!)}
        
        {/* Toolbar on hover/select */}
        {(isHovered || isSelected) && (
          <CellToolbar
            cell={cell}
            onEdit={() => onSelect(cell.id)}
            onSplitHorizontal={(pattern) => handleSplit('horizontal', pattern)}
            onSplitVertical={(pattern) => handleSplit('vertical', pattern)}
            onDelete={handleDelete}
            className="absolute -top-8 left-1/2 -translate-x-1/2 z-10"
          />
        )}
      </div>
      
      <DeleteCellDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleConfirmDelete}
        contentCount={contentCount}
      />
    </>
  );
};
